import { useEffect, useState } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { getReferralDashboard, referralLink, type ReferralDashboard } from "../lib/referral";

const money = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

export function Referrals() {
  const [data, setData] = useState<ReferralDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getReferralDashboard().then(setData).finally(() => setLoading(false));
  }, []);

  async function copy() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(referralLink(data.code));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — the field is selectable */ }
  }

  if (loading) return <p className="p-6 font-mono text-sm text-muted">Loading…</p>;
  if (!data) return <p className="p-6 text-sm text-muted">Couldn't load your referrals.</p>;

  const link = referralLink(data.code);

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-6">
      <PageHeader title="Refer & earn" subtitle="Earn 10% of every Pro subscription you refer — $10/mo for up to 12 months ($120 per referral)." />

      <Panel title="Your referral link">
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${inputClass} flex-1 font-mono text-xs`} readOnly value={link} aria-label="Referral link" onFocus={(e) => e.currentTarget.select()} data-testid="referral-link" />
          <Button onClick={copy}>{copied ? "Copied ✓" : "Copy"}</Button>
        </div>
        <p className="font-mono text-[11px] text-muted">Your code: <span className="font-bold text-ink">{data.code}</span></p>
        <p className="text-[11px] text-muted">You get credited when someone who signs up through your link subscribes to Pro — 10% of their monthly payment for up to a year. Links are attributed for {(data.attribution_window_days ?? 90) / 30} months.</p>
      </Panel>

      <dl className="grid grid-cols-3 gap-3">
        {([
          ["Signed up", data.stats.signed_up],
          ["Converted", data.stats.converted],
          ["Earned", money(data.stats.reward_total_cents)],
        ] as const).map(([label, value]) => (
          <div key={label} className="rounded-card border border-line bg-surface p-4 text-center">
            <dt className="font-mono text-[11px] uppercase tracking-widest text-muted">{label}</dt>
            <dd className="font-display text-2xl font-black tracking-tight">{value}</dd>
          </div>
        ))}
      </dl>

      <Panel title={`Your referrals (${data.referrals.length})`}>
        {data.referrals.length === 0 ? (
          <p className="font-mono text-sm text-muted">No referrals yet. Share your link to get started.</p>
        ) : (
          <ul className="grid gap-1">
            {data.referrals.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="font-bold">{r.referred_name ?? "New member"}</span>
                <span className="flex items-center gap-3">
                  {r.reward_cents > 0 && <span className="font-mono text-[11px] text-verified">{money(r.reward_cents)}</span>}
                  <StatusLabel status={r.status} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
